/**
 * The upload core behind the assessment editor's image and audio fields: pick a file, upload it,
 * hand the caller the value to persist.
 *
 * On success the caller receives the uploaded object's PATH, never the signed URL from the
 * response — see `media-asset.hook` for why. Pasting an external URL and clearing the field go
 * through the same `onChange`, so a consumer only ever deals with one string.
 *
 * It also appends that path to the document's media manifest (`store.addMediaPath`). Remove and
 * replace deliberately do NOT prune it: the same object can be referenced twice, so pruning is
 * reconciliation's job at save time, where the whole document is in view.
 *
 * Client-side gates mirror the server's rules and reuse `classifyFiles` from the DropZone kit so the
 * wording matches every other upload. They are a courtesy — the server validates again and its
 * `422` surfaces through the same error line.
 */
import { computed, ref } from 'vue'

import type { ComputedRef, Ref } from 'vue'

import { classifyFiles } from '@shared/ui/other'

import { useAssessmentTemplateUploadMediaApi } from '../../api/assessment-templates/upload-media.hook'
import { useAssessmentEditorStore } from './store'
import { useAssessmentMediaSrc, rememberAssessmentMediaLink } from './media-asset.hook'

interface UseAssessmentMediaUploaderOptions {
  value: () => string | undefined
  onChange: (value: string) => void
  /** Extensions — what both the file dialog's `accept` and `classifyFiles` speak. */
  accept: string[]
  maxSize: number
  /** Shown while the template has no row to attach media to. */
  cannotUploadHint: string
}

export interface UseAssessmentMediaUploaderReturn {
  /** Ready-to-use `src`, resolved from a path when needed. */
  refSrc: ComputedRef<string>
  refIsUploaded: ComputedRef<boolean>
  refIsResolving: Ref<boolean>
  refHasValue: ComputedRef<boolean>
  /** The external URL to show in the text field — empty for an uploaded object, whose value is a
   *  path no author should be editing by hand. */
  refExternalUrl: ComputedRef<string>
  /** Last path segment, so a caption names the file rather than the whole prefix. */
  refFileName: ComputedRef<string>
  refIsUploading: Ref<boolean>
  refProgress: Ref<number>
  /** Upload failure, rejected file, or an unresolvable path — whichever is current. */
  refError: ComputedRef<string | null>
  /** False until the template has a row to attach media to. */
  refCanUpload: ComputedRef<boolean>
  handleFiles: (files: File[]) => void
  handleUrlChange: (value: string) => void
  handleRemove: () => void
}

export const useAssessmentMediaUploader = (
  { value, onChange, accept, maxSize, cannotUploadHint }: UseAssessmentMediaUploaderOptions,
): UseAssessmentMediaUploaderReturn => {
  const store = useAssessmentEditorStore()

  const {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refError: refResolveError,
  } = useAssessmentMediaSrc(value)

  const refIsUploading = ref<boolean>(false)
  const refProgress = ref<number>(0)
  const uploadError = ref<string | null>(null)

  const refValue = computed<string>(() => {
    const raw = value()

    return typeof raw === 'string' ? raw.trim() : ''
  })

  const refHasValue = computed<boolean>(() => refValue.value.length > 0)

  const refExternalUrl = computed<string>(() => (refIsUploaded.value ? '' : refValue.value))

  const refFileName = computed<string>(() => refValue.value.split('/').pop() ?? '')

  const refCanUpload = computed<boolean>(() => store.getTemplateId !== null)

  // Upload failures win: they describe what the author just did, where a resolve error describes
  // a value that was already there.
  const refError = computed<string | null>(() => uploadError.value ?? refResolveError.value)

  const handleFiles = (files: File[]): void => {
    const file = files[0]
    if (!file || refIsUploading.value) return

    const { accepted, rejected } = classifyFiles([file], {
      accept,
      maxSize,
      remainingSlots: 1,
      existingKeys: new Set<string>(),
    })

    const refusal = rejected[0]

    if (refusal) {
      uploadError.value = refusal.reason
      return
    }

    const acceptedFile = accepted[0]
    if (!acceptedFile) return

    const templateId = store.getTemplateId

    if (templateId === null) {
      uploadError.value = cannotUploadHint
      return
    }

    uploadError.value = null

    void useAssessmentTemplateUploadMediaApi({
      templateId,
      file: acceptedFile.file,
      onStart: () => {
        refIsUploading.value = true
        refProgress.value = 0
      },
      onProgress: (percent) => {
        refProgress.value = percent
      },
      onSuccess: (media) => {
        // The editor may have loaded another template while the file was uploading; this path
        // belongs to the one it was uploaded for, not to whatever is open now.
        if (store.getTemplateId !== templateId) return

        // Seeded before the write so the preview plays from the link the response already
        // carried, rather than re-signing the same object a moment later.
        rememberAssessmentMediaLink(media)
        // Reconciliation on save would find this path anyway; appending now keeps the manifest
        // honest between saves, and this is the one moment anything knows the object is new.
        store.addMediaPath(media.path)
        onChange(media.path)
      },
      onError: (message) => {
        uploadError.value = message
      },
      onFinish: () => {
        refIsUploading.value = false
      },
    })
  }

  /** A pasted URL replaces whatever was there, uploaded object included — the object itself stays
   *  in the bucket, since there is no delete endpoint. */
  const handleUrlChange = (next: string): void => {
    uploadError.value = null
    onChange(next.trim())
  }

  const handleRemove = (): void => {
    uploadError.value = null
    onChange('')
  }

  return {
    refSrc,
    refIsUploaded,
    refIsResolving,
    refHasValue,
    refExternalUrl,
    refFileName,
    refIsUploading,
    refProgress,
    refError,
    refCanUpload,
    handleFiles,
    handleUrlChange,
    handleRemove,
  }
}
